import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Shield } from "lucide-react";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { useProfile } from "@/hooks/useProfile";
import { DrillCard } from "./DrillCard";
import { DrillPreviewDialog } from "./DrillPreviewDialog";

interface SavedDrillsProps {
  drills: any[];
  onDeleteDrill: (id: string) => void;
  onSelectDrill: (drill: any) => void;
}

const SavedDrills = ({ drills, onDeleteDrill, onSelectDrill }: SavedDrillsProps) => {
  const [selectedDrill, setSelectedDrill] = useState<any | null>(null);
  const { toast } = useToast();
  const { profile } = useProfile();

  const handlePaidFeatureClick = (featureName: string) => {
    if (!profile?.paid_account) {
      toast({
        title: "Premium Feature",
        description: `${featureName} is only available to paid users. Please upgrade your account to access this feature.`,
        variant: "destructive",
      });
      return false;
    }
    return true;
  };

  const handleDownload = (drill: any) => {
    if (!handlePaidFeatureClick("Downloading drills")) return;
    
    const drillData = JSON.stringify(drill, null, 2);
    const blob = new Blob([drillData], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement("a");
    link.href = url;
    link.download = `drill-${drill.id}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleShare = () => {
    if (!handlePaidFeatureClick("Sharing drills")) return;
    // Existing share functionality
  };

  const isAdmin = profile?.admin_user?.can_moderate_marketplace;

  return (
    <>
      <Card className="mt-6 bg-white/80 backdrop-blur-sm border border-blue-100 shadow-lg">
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>Saved Drills</CardTitle>
              <CardDescription>Your created drills</CardDescription>
            </div>
            {isAdmin && (
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4 text-blue-500" />
                <span className="text-sm text-blue-500">Admin Mode</span>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {drills.map((drill) => (
              <DrillCard
                key={drill.id}
                drill={drill}
                isAdmin={isAdmin}
                isCreator={drill.creator_id === profile?.id}
                onPreview={setSelectedDrill}
                onSelect={onSelectDrill}
                onDownload={handleDownload}
                onShare={handleShare}
                onDelete={onDeleteDrill}
              />
            ))}
            {drills.length === 0 && (
              <div className="col-span-2 text-center py-8 text-gray-500">
                No drills created yet
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <DrillPreviewDialog
        drill={selectedDrill}
        open={!!selectedDrill}
        onOpenChange={(open) => !open && setSelectedDrill(null)}
      />
    </>
  );
};

export default SavedDrills;
